// Copyright 1999-2026. WebPros International GmbH. All rights reserved.

import { Component } from './component';
import prepareUrl from './prepareUrl';
import escapeHtml from './escapeHtml';

/**
 * Example usage:
 *
 *     @example
 *     new Button({
 *         renderTo: document.body,
 *         title: 'Add a Customer',
 *         cls: 's-btn sb-add-new-customer',
 *         isNew: true,
 *         newTitle: 'New',
 *         handler() {
 *             alert('You clicked the button!');
 *         },
 *     });
 */
export class Button extends Component {
    /**
     * @cfg {String} title=""
     */
    /**
     * @cfg {String} rightIcon=""
     */
    /**
     * @cfg {String|Function} handler
     */
    /**
     * @cfg {String} href
     */
    /**
     * @cfg {String} target
     */
    /**
     * @cfg {String} disabledClass=btn-disabled
     */
    /**
     * @cfg {Boolean} isNew=false
     */
    /**
     * @cfg {String} newTitle=""
     */
    /**
     * @cfg {String} tag=a
     */
    /**
     * @cfg {String} role=button
     */
    /**
     * @cfg {String} type
     */
    /**
     * @cfg {Number} tabIndex
     */

    _initConfiguration(config) {
        super._initConfiguration({ tag: 'a', ...config });

        this._isButtonTag = 'button' === String(this._tag).toLowerCase();

        this._handler = this._getConfigParam('handler', false);
        if (typeof this._handler === 'string') {
            // eslint-disable-next-line no-eval
            eval(`this._handler = ${this._handler}`);
        }
        this._title = this._getConfigParam('title', '');
        this._rightIcon = this._getConfigParam('rightIcon', '');
        this._cls = this._getConfigParam('cls', `b-btn${!this._title ? ' btn-icon-only' : ''}`);
        this._disabledClass = this._getConfigParam('disabledClass', 'btn-disabled');
        this._isNew = this._getConfigParam('isNew', false);
        this._newTitle = this._getConfigParam('newTitle', '');
    }

    _initComponentElement() {
        super._initComponentElement();

        const ariaLabel = this._getConfigParam('aria-label', '') || this._getConfigParam('description', '');
        if (ariaLabel) {
            this._addAttribute('aria-label', ariaLabel);
        }

        this._addAttribute('role', this._getConfigParam('role', 'button'));
        this._addAttribute('type', this._getConfigParam('type', this._isButtonTag ? 'button' : null));
        this._addAttribute('tabindex', this._getConfigParam('tabIndex', null));

        this._componentElement.innerHTML = (
            '<i><i><i>' +
                `<span>${escapeHtml(this._title)}${this._isNew ? ` <span class="badge-new">${this._newTitle}</span>` : ''}${this._rightIcon}</span>` +
            '</i></i></i>'
        );

        this._initVisibility();
    }

    _initVisibility() {
        this._disabled = this._getConfigParam('disabled', false);

        if (this._disabled) {
            this._disabled = false;
            this.disable();
        } else {
            this._disabled = true;
            this.enable();
        }
    }

    _addAttribute(name, value) {
        if ('undefined' !== typeof value && value !== null) {
            this._componentElement.setAttribute(name, value);
        }
    }

    enable() {
        if (!this._disabled) {
            return;
        }
        this._disabled = false;

        let classNames = this._config.addCls || '';
        if (typeof classNames === 'string') {
            classNames = classNames.split(' ').filter(className => className);
        } else {
            classNames = [classNames];
        }
        classNames.forEach(item => {
            this._componentElement.classList.add(item);
            this._componentElement.classList.remove(`${item}-disabled`);
        });
        this._componentElement.classList.remove(this._disabledClass);

        this._addAttribute('href', prepareUrl(this._getConfigParam('href', this._isButtonTag ? null : 'javascript:;')));
        this._addAttribute('target', this._getConfigParam('target', null));

        this._componentElement.removeAttribute('aria-disabled');

        this._addProgressHandler();
        if (this._handler) {
            this.addEventObserver('click', this._handler);
        }
    }

    disable() {
        if (this._disabled) {
            return;
        }
        this._disabled = true;

        this._componentElement.classList.add(this._disabledClass);
        this._addAttribute('aria-disabled', 'true');

        let classNames = this._config.addCls || '';
        if (typeof classNames === 'string') {
            classNames = classNames.split(' ').filter(className => className);
        } else {
            classNames = [classNames];
        }
        classNames.forEach(item => {
            this._componentElement.classList.add(`${item}-disabled`);
            this._componentElement.classList.remove(item);
        });
        this._componentElement.removeAttribute('href');
        this._componentElement.removeAttribute('target');

        if (this._handler) {
            this.removeEventObserver('click', this._handler);
        }
    }

    setTabIndex(tabIndex) {
        this._addAttribute('tabindex', tabIndex);
    }

    _addProgressHandler() {
        // basic empty implementation
    }
}
